import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight, Star } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import { IMAGES } from "@/data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const headingLines = ["E-booki", "edukacyjne"];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMock = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={TESTIDS.hero.section}
      className="relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32 px-5 sm:px-8"
    >
      {/* ambient blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-violet-soft/50 blur-[120px]" />
      <div className="pointer-events-none absolute top-40 -right-20 h-[380px] w-[380px] rounded-full bg-blush/60 blur-[120px]" />

      <div className="relative mx-auto max-w-6xl grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-500 text-violet-deep shadow-[0_4px_20px_rgb(0,0,0,0.04)] mb-8"
          >
            <Star size={13} className="fill-violet-deep" /> Materiały cyfrowe o spektrum autyzmu
          </motion.div>

          <h1 className="font-display font-800 text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.92] text-ink">
            {headingLines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
                <motion.span
                  className="block"
                  variants={line}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? <span className="text-violet-deep">{l}</span> : l}
                </motion.span>
              </span>
            ))}
          </h1>

          <p className="mt-7 max-w-md text-lg leading-relaxed text-ink/60">
            Praktyczne ebooki dla nauczycieli i rodziców dzieci w spektrum autyzmu.
            Pobierz i korzystaj natychmiast.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid={TESTIDS.hero.cta}
              onClick={() => scrollTo("produkty")}
              className="group inline-flex items-center gap-2 rounded-full bg-violet text-white text-lg font-500 px-8 py-4 shadow-[0_10px_40px_-8px_rgb(167,139,250,0.7)] hover:bg-violet-deep hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              Przejdź do sklepu
              <ArrowDownRight size={20} className="group-hover:rotate-45 transition-transform duration-300" />
            </button>
          </div>
        </div>

        <div className="lg:col-span-5">
          <motion.div style={{ y: yMock, rotate }} className="relative mx-auto w-full max-w-xl">
            <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-violet-soft/50 to-blush/60 blur-3xl" />

            <div data-testid={TESTIDS.hero.mockup} className="relative z-10 flex items-end justify-center py-6">
              <img
                src={IMAGES.heroCovers.chlopcy}
                alt="E-book: Jak rozpoznać autyzm u chłopców i mężczyzn"
                className="relative z-10 w-[26%] translate-x-8 translate-y-6 -rotate-[14deg] rounded-xl shadow-[0_25px_60px_-20px_rgb(40,40,90,0.45)] ring-1 ring-black/5"
              />
              <img
                src={IMAGES.heroCovers.kobiety}
                alt="E-book: Jak rozpoznać autyzm u dziewczynek i kobiet"
                className="relative z-20 -mx-3 w-[30%] -translate-y-1 -rotate-[4deg] rounded-xl shadow-[0_35px_80px_-24px_rgb(90,60,140,0.5)] ring-1 ring-black/5"
              />
              <div className="relative z-20 -mx-3 w-[30%] -translate-y-1 rotate-[4deg]">
                <span className="absolute -top-2 left-1/2 z-30 -translate-x-1/2 rounded-full bg-emerald-500 px-2.5 py-0.5 text-[9px] font-700 uppercase tracking-wide text-white shadow-lg">
                  Nowość
                </span>
                <img
                  src={IMAGES.heroCovers.szkola}
                  alt="Workbook: Szkoła w spektrum"
                  className="w-full rounded-xl shadow-[0_35px_80px_-24px_rgb(40,100,60,0.45)] ring-1 ring-black/5"
                />
              </div>
              <img
                src={IMAGES.heroCovers.wakacje}
                alt="E-book wakacyjny: Wakacje w spektrum"
                className="relative z-10 w-[26%] -translate-x-8 translate-y-6 rotate-[14deg] rounded-xl shadow-[0_25px_60px_-20px_rgb(120,60,90,0.4)] ring-1 ring-black/5"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
